import axios from 'axios';

import { FEATURE_TOGGLES_URL } from 'constants/api';

/**
 * Fetches the runtime configuration switchboard: every setting the API declares, grouped by
 * category, with its current state and who last changed it.
 *
 * SuperAdmin only — the API returns 403 for anyone else.
 *
 * The response reports the environment and AWS region it was read from. The two regions have
 * independent tables, so a setting changed in one is unchanged in the other.
 *
 * The API names its collection `toggles`, from when this screen carried only feature toggles.
 * It is normalized to `settings` here so the old name stops at the service boundary instead of
 * spreading back through the view.
 *
 * @returns {Promise<{environment: string, region: string, tableName: string, settings: Array}>}
 */
export const getRuntimeConfig = async () => {
  try {
    const { data } = await axios.get(FEATURE_TOGGLES_URL);
    const { toggles, ...switchboard } = data ?? {};
    return { ...switchboard, settings: toggles ?? [] };
  } catch (error) {
    errorProcessing('fetching runtime configuration', error);
    throw error;
  }
};

/**
 * Turns one setting on or off in the region serving this request.
 *
 * Takes effect without a deploy, and setting enabled back to its previous value is a full
 * rollback.
 *
 * @param {string} settingKey Setting key from the catalog, e.g. 'reportCutover.TRANSACTION_ACTIVITY'
 * @param {boolean} enabled true to turn the setting on
 * @returns {Promise<object>} The setting's updated switchboard row
 */
export const updateRuntimeConfigSetting = async (settingKey, enabled) => {
  try {
    const { data } = await axios.put(`${FEATURE_TOGGLES_URL}/${encodeURIComponent(settingKey)}`, { enabled });
    return data;
  } catch (error) {
    errorProcessing(`updating runtime configuration setting ${settingKey}`, error);
    throw error;
  }
};

const errorProcessing = (context, error) => {
  if (error.response) {
    console.error(`Error ${context}: Server error response received, `, error.response.data);
  } else if (error.request) {
    console.error(`Error ${context}: No response received from server`);
  } else {
    console.error(`Error ${context}:`, error.message);
  }
};

export default { getRuntimeConfig, updateRuntimeConfigSetting };
